import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Button, Container } from '@mui/material';
import {
  needsConsentPrompt,
  acceptConsent,
  declineConsent,
} from '../../utils/cookieConsent';
import { initializeAnalytics, track, page, resetAnalytics } from '../../services/analytics';
import { removeGTM } from '../../services/gtm';
import { captureCampaignData } from '../../utils/campaignCapture';
import styles from './CookieBanner.module.css';

/**
 * CookieBanner Component
 *
 * Displays a cookie consent banner at the bottom of the screen.
 * Only shows when consent is unset or expired.
 * Cannot be dismissed without making a choice.
 *
 * Features:
 * - Captures campaign data on mount
 * - Tracks consent decisions
 * - Initializes analytics on accept
 * - Mobile responsive
 * - GDPR/CCPA compliant
 */
function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if we need to show the banner
    const needsPrompt = needsConsentPrompt();
    console.log('🍪 Cookie banner - needs consent prompt:', needsPrompt);

    if (needsPrompt) {
      setShowBanner(true);

      // Capture campaign data (before consent, for later use)
      captureCampaignData();
    }
  }, []);

  const handleAccept = async () => {
    try {
      // Set consent
      acceptConsent();

      // Initialize analytics and wait for it to be ready
      await initializeAnalytics();

      // Track the consent decision (now that analytics is initialized)
      track('Cookie Consent Accepted', {
        timestamp: new Date().toISOString(),
        page: window.location.pathname,
      });

      // Record the current page view with campaign context
      page(undefined, {
        path: `${window.location.pathname}${window.location.search}`,
      });

      // Hide banner
      setShowBanner(false);
    } catch (error) {
      console.error('Failed to initialize analytics after consent:', error);
      // Still hide banner even if analytics initialization fails
      setShowBanner(false);
    }
  };

  const handleDecline = () => {
    // Set consent to declined
    declineConsent();

    // Stop any analytics that may have been initialized previously
    try {
      resetAnalytics();
    } catch (error) {
      console.error('Failed to reset analytics on decline:', error);
    }
    try {
      removeGTM();
    } catch (error) {
      console.error('Failed to remove GTM on decline:', error);
    }

    // Hide banner
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <Box className={styles.bannerContainer}>
      <Container maxWidth="lg">
        <Box className={styles.bannerContent}>
          <Box className={styles.textSection}>
            <Typography variant="body1" className={styles.bannerText}>
              We use cookies and similar technologies to improve your experience,
              analyze site usage, and assist in our marketing efforts. By clicking
              "Accept All Cookies", you agree to the storing of cookies on your
              device.{' '}
              <Link to="/privacy-policy" className={styles.learnMoreLink}>
                Learn more in our Privacy Policy
              </Link>
            </Typography>
          </Box>

          <Box className={styles.buttonSection}>
            <Button
              variant="outlined"
              onClick={handleDecline}
              className={styles.declineButton}
              sx={{
                color: '#fff',
                borderColor: '#fff',
                '&:hover': {
                  borderColor: '#fff',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Decline
            </Button>
            <Button
              variant="contained"
              onClick={handleAccept}
              className={styles.acceptButton}
              sx={{
                backgroundColor: '#c91e1e',
                '&:hover': {
                  backgroundColor: '#a01818',
                },
              }}
            >
              Accept All Cookies
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default CookieBanner;
